import type {ComponentType} from "react";
import type React from "react";
import {useMemo} from "react";
import type {
    ActionMeta,
    GroupBase,
    OnChangeValue,
    PropsValue,
    Props as ReactSelectProps,
    StylesConfig
} from "react-select";
import type {AsyncProps} from "react-select/async";
import AsyncSelect from "react-select/async";
import WindowedSelect from "react-windowed-select";

import {memoWithType} from "../../react/utils/memoWithType";
import shadows from "../../theme/shadows";

import {DropdownIndicator} from "./DropdownIndicator";
import {MultiValueLabel} from "./MultiValueLabel";
import {Option} from "./Option";
import {SingleValue} from "./SingleValue";

export interface OptionType<TValue = string | number | boolean> {
    label: string;
    value: TValue;
    Icon?: React.ReactElement<any>;
    leftIcon?: React.ReactElement<any>;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    color?: "white";
    isFixed?: boolean;
}

type AsycnOptions<T extends OptionType, IsMulti extends boolean> = {
    loadOptions: NonNullable<AsyncProps<T, IsMulti, GroupBase<T>>["loadOptions"]>;
    options?: NonNullable<ReactSelectProps<T>["options"]>;
};

type SyncOptions<Option extends OptionType> = {
    options: NonNullable<ReactSelectProps<Option>["options"]>;
};

export type Props<Option extends OptionType, IsMulti extends boolean = false> = StrictUnion<
    AsycnOptions<Option, IsMulti> | SyncOptions<Option>
> & {
    value?: PropsValue<Option> | null;
    isMulti?: IsMulti;
    variant?: "outline" | "solid" | "ghost";
    error?: boolean;
    maxOptions?: number;
    menuIsOpen?: boolean;
    isInvalid?: boolean;
    hideDropdownIndicator?: boolean;
    testID?: string;
    onChange?: (selectedOption: OnChangeValue<Option, IsMulti>, action: ActionMeta<Option>) => void;
} & ReactSelectProps<Option, IsMulti>;

const emptyObject = {};

function Select<Option extends OptionType, IsMulti extends boolean = false>({
    options,
    value,
    isMulti,
    variant = "outline",
    styles: stylesProps = emptyObject,
    error,
    isInvalid = false,
    loadOptions,
    testID,
    ...props
}: Props<Option, IsMulti>) {
    // since `react-windowed-select` is wrongly exported without the generic types
    // which should be fixed
    const WindowedSelectWithTypes = WindowedSelect as ComponentType<
        ReactSelectProps<Option, IsMulti, GroupBase<Option>> & {
            windowThreshold?: number;
        }
    >;
    const {singleValue, multiValue, multiValueRemove, ...rest} = stylesProps;

    const variantStyles = useMemo(() => {
        const styles = {
            solid: {
                control: {
                    backgroundColor: "var(--color-blueGray-100)",
                    borderColor: error ? "var(--color-error-600)" : "transparent",
                    borderWidth: "1px"
                }
            },
            outline: {
                control: {
                    backgroundColor: "var(--color-white)",
                    borderColor: error ? "var(--color-error-600)" : "var(--color-blueGray-200)",
                    borderWidth: "1px"
                }
            },
            ghost: {
                control: {
                    backgroundColor: "transparent",
                    borderColor: "transparent",
                    borderWidth: "0px"
                }
            }
        };
        return styles[variant];
    }, [error, variant]);

    const styles = useMemo(() => {
        const {spread, textField} = shadows;

        return {
            control: (baseStyles, state) => ({
                ...baseStyles,
                borderRadius: "4px",
                ...variantStyles.control,
                ...(state.isFocused
                    ? {
                          boxShadow: `${spread.shadowOffset.width}px ${spread.shadowOffset.height}px ${spread.shadowRadius}px ${spread.elevation}px ${spread.shadowColor}80`
                      }
                    : {
                          boxShadow: `${textField.shadowOffset.width}px ${textField.shadowOffset.height}px ${textField.shadowRadius}px ${textField.elevation}px ${textField.shadowColor}1A`
                      }),
                ...(variant === "ghost" && {
                    boxShadow: "none"
                }),
                opacity: state.isDisabled ? 0.5 : 1,
                ":hover": {
                    borderColor: isInvalid ? "var(--color-danger-600)" : "var(--color-blueGray-200)"
                },
                borderColor: "var(--color-blueGray-200)",
                lineHeight: "18px", // To match Text/sm line height
                fontSize: "14px", // To match Text/sm line height
                fontWeight: "500", // To match Text/sm line height
                minHeight: "auto",
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 12,
                paddingRight: 12,
                ...(isInvalid && {borderColor: "var(--color-danger-600)"})
            }),
            menu: baseStyles => ({
                ...baseStyles,
                width: "unset",
                minWidth: "100%",
                maxWidth: "300px",
                borderRadius: "4px",
                borderColor: "var(--color-blueGray-200)",
                borderWidth: "1px",
                borderStyle: "solid",
                overflow: "hidden",
                marginLeft: "1px",
                marginRight: "1px"
            }),
            menuList: baseStyles => ({
                ...baseStyles,
                padding: 0,
                backgroundColor: "var(--color-background-0)"
            }),
            option: baseStyles => ({
                ...baseStyles,
                color: "var(--color-blueGray-800)",
                fontWeight: "500",
                cursor: "pointer",
                "&:hover": {
                    backgroundColor: "var(--color-blueGray-100)"
                },
                padding: "12px"
            }),
            indicatorSeparator: baseStyles => ({
                ...baseStyles,
                backgroundColor: "transparent"
            }),
            dropdownIndicator: baseStyles => ({
                ...baseStyles,
                color: "var(--color-blueGray-800)",
                padding: 0
            }),
            valueContainer: baseStyles => ({
                ...baseStyles,
                padding: 0,
                flexWrap: isMulti ? "wrap" : "nowrap"
            }),
            singleValue: (baseStyles, state) => {
                return {
                    ...baseStyles,
                    position: "static",
                    transform: "unset",
                    maxWidth: "100%",
                    color: state.isDisabled
                        ? "var(--color-blueGray-400)"
                        : "var(--color-blueGray-800)"
                };
            },
            groupHeading: baseStyles => {
                return {
                    ...baseStyles,
                    marginRight: "8px",
                    marginLeft: "8px"
                };
            },
            placeholder: baseStyles => ({
                ...baseStyles,
                maxWidth: "100%",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                color: "var(--color-blueGray-400)",
                margin: 0
            }),
            input: baseStyles => ({
                ...baseStyles,
                margin: 0,
                padding: 0
            }),
            clearIndicator: baseStyles => {
                return {
                    ...baseStyles,
                    display: "flex",
                    alignItems: "center",
                    padding: 0,
                    width: "16px",
                    height: "16px"
                };
            },
            multiValue: (base, state) => {
                return {
                    ...base,
                    borderRadius: "2px",
                    lineHeight: "15px",
                    backgroundColor: "var(--color-blueGray-50)",
                    border: "1px solid var(--color-blueGray-200)",
                    padding: "0px 4px",
                    ...(state.data.isFixed && {backgroundColor: "gray"})
                };
            },
            multiValueRemove: (base, state) => {
                return {
                    ...base,
                    padding: 0,
                    borderRadius: "2px",
                    marginLeft: "2px",
                    color: "var(--color-blueGray-600)",
                    ":hover": {
                        color: "var(--color-blueGray-600)",
                        backgroundColor: "var(--color-blueGray-100)"
                    },
                    ...(state.data.isFixed && {display: "none"})
                };
            },
            ...rest
        } as StylesConfig<Option, IsMulti>;
    }, [isInvalid, rest, variantStyles, isMulti, variant]);

    if (loadOptions) {
        return (
            <AsyncSelect<Option, IsMulti>
                defaultOptions
                components={{
                    Option,
                    SingleValue,
                    DropdownIndicator,
                    MultiValueLabel
                }}
                id={testID}
                isMulti={isMulti}
                loadOptions={loadOptions}
                styles={styles}
                value={value}
                {...props}
            />
        );
    }

    return (
        <WindowedSelectWithTypes
            // the component doesn't support to pass `data-testid`
            components={{
                Option,
                SingleValue,
                DropdownIndicator,
                MultiValueLabel
            }}
            id={testID}
            isMulti={isMulti}
            options={options}
            styles={styles}
            value={value}
            {...props}
        />
    );
}

export default memoWithType(Select);
